/**
 * floating-visibility.ts — permite que una sección pida ocultar los botones
 * flotantes (WhatsApp y "volver al inicio") mientras se ve en pantalla, para
 * que no la tapen. Ej.: el reproductor de la bienal (CEC-261009-83b6).
 *
 * Cuenta pedidos (no un booleano) para que dos secciones a la vez no se pisen.
 */
import { useEffect, type RefObject } from 'react';
import { create } from 'zustand';

interface FloatingVisibilityState {
  /** Secciones que hoy piden ocultar los botones flotantes. */
  hideRequests: number;
  request: () => void;
  release: () => void;
}

export const useFloatingVisibilityStore = create<FloatingVisibilityState>((set) => ({
  hideRequests: 0,
  request: () => set((s) => ({ hideRequests: s.hideRequests + 1 })),
  release: () => set((s) => ({ hideRequests: Math.max(0, s.hideRequests - 1) })),
}));

/** `true` cuando alguna sección pidió ocultar los botones flotantes. */
export function useFloatingHidden(): boolean {
  return useFloatingVisibilityStore((s) => s.hideRequests > 0);
}

/**
 * Pide ocultar los botones flotantes mientras el elemento de `ref` se ve en
 * pantalla, y los devuelve al salir de la pantalla o al desmontarse.
 */
export function useHideFloatingWhileVisible(ref: RefObject<Element | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const { request, release } = useFloatingVisibilityStore.getState();
    let pidiendo = false;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible && !pidiendo) {
        pidiendo = true;
        request();
      } else if (!visible && pidiendo) {
        pidiendo = false;
        release();
      }
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (pidiendo) release();
    };
  }, [ref]);
}
